import { useState } from 'react';
import { ApplicationFormDeliverySchedulePanel } from '../../components/forms/settings/ApplicationFormDeliverySchedulePanel';
import { ApplicationFormNotificationPanel } from '../../components/forms/settings/ApplicationFormNotificationPanel';
import { ApplicationFormOptionsPanel } from '../../components/forms/settings/ApplicationFormOptionsPanel';
import { AfSettingsSection } from '../../components/forms/settings/AfSettingsSection';
import { DemDeliverySettingsPanel } from '../../components/dem/DemDeliverySettingsPanel';
import { DemCffSettingsPanel } from '../../components/dem/DemCffSettingsPanel';
import { useAdminHubContext } from '../../contexts/AdminHubContext';
import {
  canManageDemConfigInAdminHub,
  canManageFormsConfigInAdminHub,
} from '../../lib/adminHubPermissions';

type AfSettingsSectionId =
  | 'notifications'
  | 'delivery'
  | 'deliveries'
  | 'cff'
  | 'options'
  | 'certificates';

export function ApplicationFormsSettingsPage({
  embeddedInAdminHub = false,
}: {
  embeddedInAdminHub?: boolean;
}) {
  const hubCtx = useAdminHubContext();
  const actorRole = hubCtx?.actorRole ?? 'user';
  const canForms = canManageFormsConfigInAdminHub(actorRole);
  const canDem = canManageDemConfigInAdminHub(actorRole);
  const canCff = canForms || canDem;

  const sections: { id: AfSettingsSectionId; label: string }[] = [
    ...(canForms
      ? ([
          { id: 'notifications', label: 'Email' },
          { id: 'delivery', label: 'Delivery schedule' },
          { id: 'options', label: 'Dropdowns' },
          { id: 'certificates', label: 'Certificate library' },
        ] as const)
      : []),
    ...(canDem ? ([{ id: 'deliveries', label: 'Deliveries' }] as const) : []),
    ...(canCff ? ([{ id: 'cff', label: 'CFF' }] as const) : []),
  ];

  const defaultSection: AfSettingsSectionId = canForms
    ? 'notifications'
    : canDem
      ? 'deliveries'
      : 'cff';
  const [section, setSection] = useState<AfSettingsSectionId>(defaultSection);
  const activeSection = sections.some((s) => s.id === section) ? section : defaultSection;

  if (!canForms && !canDem) {
    return <p className="settings-empty">You cannot manage forms & deliveries.</p>;
  }

  return (
    <div
      className={`application-forms-settings${embeddedInAdminHub ? ' application-forms-settings--embedded' : ''}`}
    >
      {sections.length > 1 ? (
        <nav className="af-settings-nav" aria-label="Forms & deliveries sections">
          {sections.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`af-settings-nav__tab${activeSection === item.id ? ' af-settings-nav__tab--active' : ''}`}
              aria-current={activeSection === item.id ? 'page' : undefined}
              onClick={() => setSection(item.id)}
            >
              <span className="af-settings-nav__label">{item.label}</span>
            </button>
          ))}
        </nav>
      ) : null}

      {canForms && activeSection === 'notifications' ? <ApplicationFormNotificationPanel /> : null}
      {canForms && activeSection === 'delivery' ? <ApplicationFormDeliverySchedulePanel /> : null}
      {canForms && activeSection === 'options' ? <ApplicationFormOptionsPanel /> : null}
      {canForms && activeSection === 'certificates' ? (
        <AfSettingsSection
          title="Certificate library"
          description="Moved to the Purchasing portal."
          badge="CTR"
        >
          <p className="page-lead">
            Manage SFA / ISO and item certificates under{' '}
            <strong>Choose Portal → Purchasing → Items</strong>. Grant access in User management →
            Purchasing role.
          </p>
          <p>
            <a className="btn-primary btn-sm" href="/purchasing/items">
              Open Purchasing Items
            </a>
          </p>
        </AfSettingsSection>
      ) : null}
      {canDem && activeSection === 'deliveries' ? <DemDeliverySettingsPanel /> : null}
      {canCff && activeSection === 'cff' ? <DemCffSettingsPanel canEdit={canCff} /> : null}
    </div>
  );
}
